import { useEffect, useRef, useState } from "react";

const stats = [
  { end: 25, prefix: "15-", suffix: "%", label: "Improvement in agent productivity" },
  { end: 3, suffix: "x", label: "Faster resolution of high-risk accounts" },
  { end: 100, suffix: "%", label: "Reduced compliance exposure" },
  { end: 40, suffix: "%", label: "Lower operational overhead" },
  { end: 2, suffix: "x", label: "Improved borrower engagement" },
  { text: "Hours", label: "Deployment time (not weeks)" },
];

function StatCard({ stat }) {
  const [count, setCount] = useState(0);
  const hasAnimatedRef = useRef(false);
  const cardRef = useRef(null);

  useEffect(() => {
    if (stat.text) return; // no counter for pure text stats
    if (typeof IntersectionObserver === "undefined") return; // SSR safety

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimatedRef.current) {
          hasAnimatedRef.current = true;
          const duration = 1800;
          const startTime = performance.now();

          const animate = (now) => {
            const elapsed = now - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
            setCount(Math.round(eased * stat.end));
            if (progress < 1) requestAnimationFrame(animate);
          };

          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.4 }
    );

    if (cardRef.current) observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, [stat]);

  const displayValue = stat.text
    ? stat.text
    : `${stat.prefix || ""}${count}${stat.suffix}`;

  return (
    <div
      ref={cardRef}
      className="bg-white/10 rounded-3xl border border-white/20 p-8 flex flex-col gap-3 items-center"
      style={{ backdropFilter: "blur(5px)" }}
    >
      <span className="text-white text-center font-semibold text-5xl md:text-6xl leading-tight">
        {displayValue}
      </span>
      <span className="text-[#f3e8ff] text-center font-normal text-lg leading-7">
        {stat.label}
      </span>
    </div>
  );
}

export default function ImpactSection() {
  return (
    <section className="bg-[#0F172A] py-24 px-5 md:px-20 overflow-hidden">
      <div className="max-w-7xl mx-auto px-0 md:px-8 flex flex-col gap-16">
        {/* Header */}
        <div className="flex flex-col gap-4 items-center justify-center">
          <span
            className="text-[#e9d5ff] text-center font-semibold text-sm leading-5 uppercase"
            style={{ letterSpacing: "0.7px" }}
          >
            Impact
          </span>
          <h2 className="text-white text-center font-['SpaceGrotesk-Bold',_sans-serif] text-4xl md:text-5xl lg:text-6xl leading-tight font-bold max-w-3xl">
            Deliver Measurable Recovery Improvements
          </h2>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {stats.map((stat) => (
            <StatCard key={stat.label} stat={stat} />
          ))}
        </div>
      </div>
    </section>
  );
}
